/** Post-completion panel for composition_import tasks — the persistent
 * "Review proposal" affordance on the task dialog itself. The snackbar the
 * ingest watcher shows is transient; this stays for as long as the task
 * record does. Reads the archived result (which carries proposal_id). */
import RateReviewIcon from '@mui/icons-material/RateReview';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { PermissionGate } from '@/components/PermissionGate';
import { consoleRouteFor } from '@/utils/gridLinks';

/** Where a materialized record can be edited — the shared content-type → route
 *  map (dialog-edited resources resolve to the `?edit=` intent their list
 *  pages consume; a tag lands on its page; a delivery method has no page of
 *  its own and renders as text). */
const editUrlFor = consoleRouteFor;

const KIND_LABELS: Record<string, string> = {
  notification: 'Notification',
  delivery_method: 'Delivery method',
  design: 'Design',
  block: 'Block',
  block_list: 'Block list',
  theme: 'Theme',
  tag: 'Tag',
  tag_category: 'Tag category',
  ruleset: 'Ruleset',
  rule: 'Rule',
  language_policy: 'Language policy',
  language_alias: 'Language alias',
  tenant_i18n: 'Tenant languages',
  integration_config: 'Integration mapping',
};

/** " (N items undone)" — the items whose writes the rollback undid. */
function undone(rolledBack: number): string {
  return rolledBack ? ` (${rolledBack} item${rolledBack === 1 ? '' : 's'} undone)` : '';
}

/** One row of the apply receipt (`created` / `updated` / `deleted`). */
interface ReceiptRecord {
  kind: string;
  model: string;
  pk: number;
  repr: string;
}

export function CompositionResultPanel({
  taskId,
  hideProposalLink = false,
}: {
  taskId: string;
  /** True when rendered ON the proposal page — the open-proposal button is noise there. */
  hideProposalLink?: boolean;
}) {
  const resultQuery = useQuery({
    queryKey: ['bgtask-result', taskId],
    queryFn: () => bgtasksApi.result(taskId),
    staleTime: Infinity,
    // The archive lands slightly after COMPLETED — tolerate early 404s.
    retry: (failureCount) => failureCount < 8,
    retryDelay: 1_500,
  });

  if (resultQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading result…
        </Typography>
      </Box>
    );
  }
  const result = resultQuery.data?.result;
  if (!result) return null;

  const action = result.action as string | undefined;
  const proposalId = result.proposal_id as number | undefined;

  if (action === 'discover') {
    const frames = (result.frames as unknown[] | undefined)?.length ?? 0;
    return (
      <Alert severity="success">
        Discovered {frames} template frame{frames === 1 ? '' : 's'} — pick the ones to import from the
        Design Imports wizard.
      </Alert>
    );
  }

  if (!proposalId) return null;
  const reviewTo = `/notifications/design-imports/proposals/${proposalId}`;

  if (action === 'apply') {
    const failed = Object.keys((result.failed as Record<string, unknown>) ?? {}).length;
    const applied = (result.applied as number | undefined) ?? 0;
    const rolledBack = (result.rolled_back as number | undefined) ?? 0;
    const error = (result.error as string | undefined) ?? '';
    // `ok` is the commit outcome. A rollback AFTER the items (the change set's S3 write, its
    // entry cap) names no failed item, only `error`; results archived before `ok` existed
    // still say so through a non-empty `failed` or `error`.
    const rolledBackApply = result.ok === false || failed > 0 || Boolean(error);
    const sections = (
      [
        ['Records created', result.created],
        ['Records updated', result.updated],
        ['Records deleted', result.deleted],
      ] as const
    )
      .map(([title, rows]) => [title, (rows as ReceiptRecord[] | undefined) ?? []] as const)
      .filter(([, rows]) => rows.length > 0);
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: sections.length ? 1.5 : 0 }}>
          <Alert severity={rolledBackApply ? 'error' : 'success'} sx={{ flex: 1, minWidth: 260 }}>
            {!rolledBackApply
              ? `${applied} item${applied === 1 ? '' : 's'} applied.`
              : failed
                ? // All-or-nothing (AIE §2): one failure undoes the whole apply.
                  `Nothing was applied — ${failed} item${failed === 1 ? '' : 's'} FAILED and the apply was rolled back` +
                  undone(rolledBack) +
                  '. The failed item carries its error on the proposal; return it to review, fix, re-accept and apply again.'
                : `Nothing was applied — the apply failed and was rolled back${undone(rolledBack)}: ` +
                  `${error || 'no reason was recorded'}. Return the proposal to review, then apply it again ` +
                  'once the cause is fixed.'}
          </Alert>
          {!hideProposalLink && (
            <PermissionGate perm="console_composition_view">
              <Button variant="outlined" component={RouterLink} to={reviewTo} startIcon={<RateReviewIcon />}>
                Open proposal
              </Button>
            </PermissionGate>
          )}
        </Box>
        {sections.map(([title, rows]) => (
          <Box key={title} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {title}
            </Typography>
            {rows.map((record) => {
              // A deleted row has nowhere to link to.
              const url = title === 'Records deleted' ? null : editUrlFor(record.model, record.pk);
              const label = KIND_LABELS[record.kind] ?? record.model;
              return (
                <Typography key={`${record.model}-${record.pk}`} variant="body2" sx={{ py: 0.25 }}>
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {label}:
                  </Box>{' '}
                  {url ? (
                    <Box component={RouterLink} to={url} sx={{ fontWeight: 600 }}>
                      {record.repr || `#${record.pk}`}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {record.repr || `#${record.pk}`}
                    </Box>
                  )}{' '}
                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                    ({record.model} #{record.pk})
                  </Box>
                </Typography>
              );
            })}
          </Box>
        ))}
      </Box>
    );
  }

  const designs = (result.designs as unknown[] | undefined)?.length ?? 1;
  const blocking = (result.blocking_questions as number | undefined) ?? 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Alert severity="success" sx={{ flex: 1, minWidth: 260 }}>
        {designs} design{designs === 1 ? '' : 's'} ingested into proposal #{proposalId}
        {blocking > 0
          ? ` — ${blocking} blocking question${blocking === 1 ? '' : 's'} need answers before it can be applied.`
          : ' — ready for review.'}
      </Alert>
      <PermissionGate perm="console_composition_view">
        <Button variant="contained" component={RouterLink} to={reviewTo} startIcon={<RateReviewIcon />}>
          Review proposal
        </Button>
      </PermissionGate>
    </Box>
  );
}
